export type EcommerceOAuthResult = {
    isSuccess: boolean
    errorMessage?: string
}

type EcommerceScopeKey = "products" | "orders" | "customers"

type EcommerceScopeLabel = {
    key: EcommerceScopeKey
    // Terse label for a toast summary ("Customers"); the fuller label names the access level for the card banner.
    short: string
    full: string
}

// The scopes probed at connection time, in the order they are surfaced. Kept as data so the toast and the
// card banner render the same human-readable names from a single source.
export const ECOMMERCE_SCOPE_LABELS: EcommerceScopeLabel[] = [
    { key: "products", short: "Products", full: "Products (read-only)" },
    { key: "orders", short: "Orders", full: "Orders (modify)" },
    { key: "customers", short: "Customers", full: "Customers (read-only)" },
]

/**
 * Returns the scopes a connection's most recent permission check found missing, as labeled entries.
 *
 * Returns an empty list when the connection has never been checked (an empty result) or every scope
 * passed, so a caller can treat "nothing to surface" and "all present" identically.
 */
export function getMissingEcommerceScopes(connection: EcommerceConnection): EcommerceScopeLabel[] {
    const result = connection.scope_check_result
    if (!result || Object.keys(result).length === 0) {
        return []
    }

    return ECOMMERCE_SCOPE_LABELS.filter((scope) => result[scope.key] === false)
}

export const OAUTH_RESULT_PARAM_NAMES = ["platform", "success", "error_type", "error_description"]

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
    provider_denied: "The store declined the authorization request.",
    missing_code: "The store's authorization response was incomplete. Please try again.",
    code_exchange: "We couldn't finish connecting the store. Please try again.",
}

/**
 * Extracts an oauth flow outcome from the query params the callback redirect carries.
 *
 * Returns null when the page load isn't an oauth return (no `success` param). Provider-supplied
 * error descriptions win over our per-error-type copy, which itself falls back to a generic message.
 */
export function extractOAuthResult(searchParams: URLSearchParams): EcommerceOAuthResult | null {
    const success = searchParams.get("success")
    if (success === null) {
        return null
    }

    if (success === "true") {
        return { isSuccess: true }
    }

    const errorType = searchParams.get("error_type") ?? ""
    const errorDescription = searchParams.get("error_description")

    return {
        isSuccess: false,
        errorMessage:
            errorDescription ||
            OAUTH_ERROR_MESSAGES[errorType] ||
            "The store connection could not be completed. Please try again.",
    }
}
